import { useState, useEffect, useCallback } from 'react'
import { TransactionList } from '@/components/dashboard/TransactionList'
import { QuickAddForm } from '@/components/forms/QuickAddForm'
import { DonutChart } from '@/components/charts/DonutChart'
import { BarChart } from '@/components/charts/BarChart'
import { LineChart } from '@/components/charts/LineChart'
import { formatCurrency } from '@/components/charts/chartUtils'

interface CategoryData {
  id: number
  name: string
  icon: string | null
  color: string
  amount: number
  percentage: number
}

interface MemberData {
  id: number
  name: string
  color: string
  amount: number
  percentage: number
}

interface MonthlyData {
  month: string
  amount: number
}

interface IncomeSummaryData {
  total: number
  byCategory: CategoryData[]
  byMember: MemberData[]
  monthlyTrend: MonthlyData[]
}

function formatMonthLabel(monthStr: string): string {
  const [year, month] = monthStr.split('-')
  const date = new Date(parseInt(year), parseInt(month) - 1)
  return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
}

export function Income() {
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [summary, setSummary] = useState<IncomeSummaryData | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchSummary = useCallback(async () => {
    try {
      const response = await fetch('/api/income-summary?period=year')
      if (response.ok) {
        const data = (await response.json()) as IncomeSummaryData
        setSummary(data)
      }
    } catch (error) {
      console.error('Error fetching income summary:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSummary()
  }, [fetchSummary, refreshTrigger])

  function handleAddSuccess() {
    setRefreshTrigger((prev) => prev + 1)
  }

  // Transform category data for donut chart
  const sourceData =
    summary?.byCategory.map((cat) => ({
      name: `${cat.icon || ''} ${cat.name}`.trim(),
      value: cat.amount,
      color: cat.color
    })) || []

  // Transform member data for bar chart
  const memberData =
    summary?.byMember.map((member) => ({
      name: member.name,
      value: member.amount,
      color: member.color
    })) || []

  // Transform monthly data for line chart
  const trendData =
    summary?.monthlyTrend.map((m) => ({
      name: formatMonthLabel(m.month),
      income: m.amount
    })) || []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-forest-dark">Income</h1>
          <p className="text-sage mt-1">Track your income sources</p>
        </div>
        <button
          onClick={() => setIsQuickAddOpen(true)}
          className="px-4 py-2 bg-forest text-white rounded-lg text-sm hover:bg-forest-dark transition-colors"
        >
          + Add Income
        </button>
      </div>

      {/* Income Sources and By Member */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Income Sources Donut Chart */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-stone/10">
          <h2 className="text-lg font-semibold text-charcoal mb-4">
            Income Sources
          </h2>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-pulse text-sage">Loading...</div>
            </div>
          ) : sourceData.length > 0 ? (
            <DonutChart
              data={sourceData}
              centerValue={formatCurrency(summary?.total || 0)}
              centerLabel="Total Income"
              height={280}
            />
          ) : (
            <div className="text-center py-12">
              <p className="text-sage">No income recorded</p>
              <p className="text-sm text-stone mt-2">
                Add income transactions to see your breakdown
              </p>
            </div>
          )}
        </div>

        {/* By Family Member Bar Chart */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-stone/10">
          <h2 className="text-lg font-semibold text-charcoal mb-4">
            By Family Member
          </h2>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-pulse text-sage">Loading...</div>
            </div>
          ) : memberData.length > 0 ? (
            <BarChart
              data={memberData}
              layout="horizontal"
              label1="Income"
              height={280}
            />
          ) : (
            <div className="text-center py-12">
              <p className="text-sage">No income recorded</p>
              <p className="text-sm text-stone mt-2">
                Add income to see contributions by family member
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Income Trend Line Chart */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-stone/10">
        <h2 className="text-lg font-semibold text-charcoal mb-4">
          Income Trend (Last 12 Months)
        </h2>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-pulse text-sage">Loading...</div>
          </div>
        ) : trendData.length > 0 ? (
          <LineChart
            data={trendData}
            dataKeys={[{ key: 'income', name: 'Income', color: '#4CAF50' }]}
            height={250}
            showLegend={false}
          />
        ) : (
          <div className="text-center py-8">
            <p className="text-sage">Not enough data for trend analysis</p>
          </div>
        )}
      </div>

      {/* Recent Income Transactions */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-stone/10">
        <h2 className="text-lg font-semibold text-charcoal mb-4">
          Recent Income
        </h2>
        <TransactionList
          type="income"
          limit={20}
          showFilters={true}
          refreshTrigger={refreshTrigger}
        />
      </div>

      {/* Quick Add Modal */}
      <QuickAddForm
        isOpen={isQuickAddOpen}
        onClose={() => setIsQuickAddOpen(false)}
        onSuccess={handleAddSuccess}
        defaultType="income"
      />
    </div>
  )
}
